import React, { useMemo } from "react";
import { Checkbox } from "@/components/Calendar/ui/checkbox";
import {
  getScheduleEventsByPharmacies,
  refreshScheduleEventsSilently,
} from "@/dispatchers/scheduleEventsDispatchers";
import { useScheduleData, type UseScheduleDataReturn } from "@/components/Calendar/scheduleData";

interface PharmaciesFilterProps {
  selectedPharmacies: string[];
  onPharmacyChange: (pharmacyId: string, checked: boolean) => void;
  onSelectAll: (checked: boolean) => void;
  isDarkMode?: boolean;
  pharmacies?: any[];
}

export function PharmaciesFilter({
  selectedPharmacies,
  onPharmacyChange,
  onSelectAll,
  isDarkMode = false,
  pharmacies: propPharmacies,
}: PharmaciesFilterProps) {
  const s = useScheduleData();
  const { pharmacies: contextPharmacies = [], currentPharmacyId } =
    (s || {}) as Partial<UseScheduleDataReturn>;
  const pharmacies = propPharmacies || contextPharmacies || [];

  const uniquePharmacies = useMemo(() => {
    const seen = new Map<string, any>();
    (pharmacies || []).forEach((p: any) => {
      const id = String(p.teamPharmacyID || p.id || "");
      if (id && !seen.has(id)) seen.set(id, p);
    });
    return Array.from(seen.values()).sort((a, b) =>
      String(a.teamPharmacyName || "").localeCompare(
        String(b.teamPharmacyName || ""),
        undefined,
        { sensitivity: "base", numeric: true },
      ),
    );
  }, [pharmacies]);

  const uniqueIds = useMemo(
    () => uniquePharmacies.map((p: any) => String(p.teamPharmacyID || p.id || "")),
    [uniquePharmacies],
  );

  const fetchScheduleEventsForSelectedPharmacies = async (
    pharmacyIds: string[],
  ) => {
    const normalizedIds = Array.from(new Set(pharmacyIds.filter(Boolean)));
    try {
      // Filter cleared - fall back to refreshing the current pharmacy's own
      // events instead of requesting the by-pharmacies endpoint with an
      // empty id list, so anything added/edited while filtered still shows.
      if (!normalizedIds.length) {
        if (currentPharmacyId) {
          await refreshScheduleEventsSilently(currentPharmacyId);
        }
        return null;
      }
      const response = await getScheduleEventsByPharmacies(normalizedIds);
      return response;
    } catch (error) {
      console.error(
        "Error fetching schedule events for selected pharmacies:",
        error,
      );
      return null;
    }
  };

  return (
    <div
      className={`rounded-lg border p-4 transition-colors duration-300 ${
        isDarkMode ? "bg-gray-700 border-gray-600" : "bg-white border-gray-200"
      }`}
    >
      <h4
        className={`text-sm font-semibold mb-3 transition-colors duration-300 ${
          isDarkMode ? "text-white" : "text-gray-900"
        }`}
      >
        Pharmacies
      </h4>
      <div className="space-y-3">
        <div className="flex items-center space-x-2">
          <Checkbox
            id="select-all-pharmacies"
            checked={
              uniqueIds.length > 0 &&
              uniqueIds.every((id) => (selectedPharmacies || []).includes(id))
            }
            onCheckedChange={async (checked) => {
              const isChecked = !!checked;
              const newArr = isChecked ? uniqueIds : [];
              onSelectAll(isChecked);
              await fetchScheduleEventsForSelectedPharmacies(newArr);
            }}
          />
          <label
            htmlFor="select-all-pharmacies"
            className={`text-sm cursor-pointer transition-colors duration-300 ${
              isDarkMode ? "text-gray-400" : "text-gray-500"
            }`}
          >
            Select All
          </label>
        </div>
        <hr
          className={`transition-colors duration-300 ${
            isDarkMode ? "border-gray-600" : "border-gray-200"
          }`}
        />
        {uniquePharmacies.map((pharmacy: any) => {
          const id = String(pharmacy.teamPharmacyID || pharmacy.id || "");
          const label = pharmacy.teamPharmacyName || "";

          return (
            <div key={id} className="flex items-center space-x-2">
              <Checkbox
                id={`pharmacy-${id}`}
                checked={(selectedPharmacies || []).includes(id)}
                onCheckedChange={async (checked) => {
                  const isChecked = !!checked;
                  const base = Array.isArray(selectedPharmacies)
                    ? selectedPharmacies
                    : [];
                  const newArr = isChecked
                    ? Array.from(new Set([...base, id]))
                    : base.filter((x) => x !== id);
                  onPharmacyChange(id, isChecked);
                  await fetchScheduleEventsForSelectedPharmacies(newArr);
                }}
              />
              <label
                htmlFor={`pharmacy-${id}`}
                className={`text-sm cursor-pointer transition-colors duration-300 ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                {label}
              </label>
            </div>
          );
        })}
      </div>
    </div>
  );
}
